import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { gql, GraphQLError } from "../lib/api";
import { SIGNIN_MUTATION } from "../lib/operations";
import { useAuth } from "../context/AuthContext";
import { ErrorMessage } from "../components/ErrorMessage";
import type { SigninResult } from "../lib/types";

// User-friendly remap of authentication error messages. The server's
// raw text (and the GraphQL envelope wrapping it) leaks internal
// shape that doesn't help the user understand what to do next.
// Anything not in this map falls through to the raw message — log
// it on the server side too so we can grow this list.
function normalizeAuthError(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes("invalid") && lower.includes("password")) {
    return "Email or password is incorrect.";
  }
  if (lower.includes("not found") || lower.includes("no such user")) {
    return "Email or password is incorrect.";
  }
  if (lower.includes("locked")) {
    return "This account is locked. Contact your administrator.";
  }
  if (lower.includes("expired")) {
    return "Your session expired. Please sign in again.";
  }
  if (lower.startsWith("graphql:") || lower.includes("graphqlerror")) {
    return "Sign in failed. Please try again.";
  }
  return raw;
}

export function SignIn() {
  const { token, decoded, initializing, setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already signed in? Bounce to the destination they came from, or
  // / by default.
  if (!initializing && token && decoded) {
    const redirectTo =
      decoded.scope === "password_change"
        ? "/change-password"
        : ((location.state as { from?: { pathname?: string } } | null)?.from
            ?.pathname ?? "/");
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const data = await gql<{ signin: SigninResult }>(SIGNIN_MUTATION, {
        email,
        password,
      });
      const result = data.signin;

      if (result.status !== "success" || !result.jwt) {
        setError(normalizeAuthError(result.message ?? "Sign in failed."));
        return;
      }

      setToken(result.jwt);

      // Routing: scoped tokens go straight to /change-password; normal
      // tokens go to /applications (or wherever they came from).
      if (result.passwordChangeRequired) {
        navigate("/change-password", { replace: true });
      } else {
        navigate("/applications", { replace: true });
      }
    } catch (err: unknown) {
      if (err instanceof GraphQLError) {
        setError(normalizeAuthError(err.message));
      } else {
        setError(
          normalizeAuthError(
            err instanceof Error
              ? err.message
              : "Sign in failed. Please try again.",
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg p-4">
      <div className="auth-shell">
        <div className="auth-panel">
          <div className="auth-panel-header px-8 py-6 text-center">
            <h1 className="brand-logo text-2xl">Payment Products</h1>
            <p className="mt-1 text-sm text-brand-muted">Team Portal</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 p-8">
            {error && <ErrorMessage message={error} />}
            <div>
              <label htmlFor="signin-email" className="label mb-1 block">
                Email
              </label>
              <input
                id="signin-email"
                name="email"
                type="email"
                // username (not email) is the autocomplete token that
                // password managers look for to know "this is the
                // identifier half of a login pair". Both Chrome and
                // 1Password key on this string specifically.
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@paymentproducts.net"
                required
                autoFocus
                maxLength={320}
              />
            </div>
            <div>
              <label htmlFor="signin-password" className="label mb-1 block">
                Password
              </label>
              <input
                id="signin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="Enter your password"
                required
                maxLength={128}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
